import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";
import { useNavigate } from "react-router-dom";
import {
  FaExchangeAlt,
  FaSyncAlt,
  FaTruck,
  FaHome,
  FaArrowUp,
  FaArrowDown,
  FaTimesCircle,
  FaSave,
} from "react-icons/fa";
import styles from "./AdjustToDispatch.module.css";

const getPNumber = (row) => row?.P_NUMBER ?? row?.p_number ?? row?.P_Number ?? "";

const pickField = (row, ...keys) => {
  if (!row) return "";
  for (const key of keys) {
    const direct = row[key];
    if (direct != null && String(direct).trim() !== "") return direct;
  }
  const lowerMap = Object.fromEntries(
    Object.entries(row).map(([k, v]) => [String(k).toLowerCase(), v])
  );
  for (const key of keys) {
    const v = lowerMap[String(key).toLowerCase()];
    if (v != null && String(v).trim() !== "") return v;
  }
  return "";
};

const formatDate = (v) => {
  if (v == null || v === "") return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-GB");
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "44px",
    borderRadius: "12px",
    borderColor: state.isFocused ? "#38bdf8" : "#d8e4f0",
    boxShadow: state.isFocused ? "0 0 0 4px rgba(14,165,233,0.14)" : "none",
    fontSize: "0.94rem",
  }),
  menu: (base) => ({ ...base, zIndex: 9999, borderRadius: "12px", overflow: "hidden" }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.9rem",
    fontWeight: state.isSelected ? 800 : 500,
  }),
};

const AdjustToDispatch = () => {
  const user = useSelector(selectUser);
  const token = user?.token;
  const emp_id = user?.emp_id;
  const navigate = useNavigate();

  const topRef = useRef(null);
  const bottomRef = useRef(null);

  const [vehicles, setVehicles] = useState([]);
  const [selectedPNumber, setSelectedPNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  const [popup, setPopup] = useState({ open: false, title: "", message: "" });

  const showPopup = (message, title = "Notice") =>
    setPopup({ open: true, title, message });
  const closePopup = () => setPopup({ open: false, title: "", message: "" });

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await axios.post("/api/wbauto/adjustToDispatch/list", {
        emp_id,
        token,
      });
      const rows = Array.isArray(res.data) ? res.data : [];
      setVehicles(rows);
      setSelectedPNumber("");

      if (rows.length === 0) {
        setToast("No eligible vehicles found.");
      }
    } catch (err) {
      setVehicles([]);
      showPopup(
        err?.response?.data?.message || "Failed to load vehicles.",
        "Error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  const vehicleOptions = useMemo(
    () =>
      vehicles
        .filter((r) => getPNumber(r))
        .map((r) => ({
          value: String(getPNumber(r)),
          label: `${r.P_TRUCK || "-"} — ${r.P_DOCNUMBER || "-"} — ${r.P_Item_name || "-"}`,
          row: r,
        })),
    [vehicles]
  );

  const selected = useMemo(
    () =>
      vehicles.find((r) => String(getPNumber(r)) === String(selectedPNumber)) ||
      null,
    [vehicles, selectedPNumber]
  );

  const handleCancel = () => setSelectedPNumber("");

  const handleSubmit = async () => {
    if (!selectedPNumber || !selected) {
      showPopup("Select a vehicle first.", "Validation");
      return;
    }

    const challanWt = selected.P_CHALLAN_WT;
    if (challanWt == null || String(challanWt).trim() === "") {
      showPopup("Challan weight is missing for this vehicle.", "Validation");
      return;
    }

    const partyTr = pickField(selected, "P_PartyTrWt", "P_PARTYTRWT", "p_partytrwt");
    const partyGr = pickField(selected, "P_PartyGrWt", "P_PARTYGRWT", "p_partygrwt");
    const hasParty =
      partyTr !== "" &&
      partyGr !== "" &&
      Number.isFinite(Number(partyTr)) &&
      Number.isFinite(Number(partyGr));

    let confirmMsg = `Set Net Weight = Challan Weight (${challanWt}) for ${
      selected.P_TRUCK || "this vehicle"
    }?`;

    if (hasParty) {
      const partyNet = Math.abs(Number(partyGr) - Number(partyTr));
      const matched = Math.abs(partyNet - Number(challanWt)) <= 0.01;
      confirmMsg = matched
        ? `Party tare/gross match challan.\nUpdate 1st Wt = Party Gross (${partyGr}), 2nd Wt = Party Tare (${partyTr}), Net = Challan (${challanWt})?`
        : `Party tare/gross do NOT match challan.\nUpdate 1st Wt = 1st + Challan, 2nd Wt = current 1st, Net = Challan (${challanWt})?`;
    } else {
      confirmMsg = `Update 1st Wt = 1st + Challan, 2nd Wt = current 1st, Net = Challan (${challanWt})?`;
    }

    if (!window.confirm(confirmMsg)) return;

    setSaving(true);
    try {
      const res = await axios.post("/api/wbauto/adjustToDispatch/submit", {
        emp_id,
        token,
        pNumber: Number(selectedPNumber),
      });

      if (res.data?.success) {
        setToast(`✅ ${res.data.message}`);
        await fetchList();
      } else {
        showPopup(res.data?.message || "Update failed.", "Error");
      }
    } catch (err) {
      showPopup(err?.response?.data?.message || "Update failed.", "Error");
    } finally {
      setSaving(false);
    }
  };

  if (!token) {
    return (
      <div className={styles.page}>
        <div className={styles.loginBox}>
          <FaTruck className={styles.loginIcon} />
          <p>You must be logged in.</p>
          <button className={styles.primaryBtn} onClick={() => navigate("/")}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div ref={topRef} />

      {toast && <div className={styles.toast}>{toast}</div>}

      {popup.open && (
        <div className={styles.modalBackdrop} onClick={closePopup}>
          <div className={styles.modalSheet} onClick={(e) => e.stopPropagation()}>
            <h4>{popup.title}</h4>
            <p>{popup.message}</p>
            <button className={styles.primaryBtn} onClick={closePopup}>
              OK
            </button>
          </div>
        </div>
      )}

      <div className={styles.hero}>
        <div className={styles.heroIcon}>
          <FaExchangeAlt />
        </div>
        <div>
          <h2>Adjust to Challan</h2>
          <p>Select a QC rejected vehicle for purchase return.</p>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.sectionTitle}>Select Vehicle</div>

        <Select
          styles={selectStyles}
          isSearchable
          isClearable
          isDisabled={loading || saving}
          placeholder={loading ? "Loading..." : "Search Vehicle / Doc / Item"}
          value={vehicleOptions.find((o) => o.value === selectedPNumber) || null}
          options={vehicleOptions}
          filterOption={(option, input) => {
            const q = String(input || "").toUpperCase();
            const r = option.data.row;
            return (
              String(r.P_TRUCK || "").toUpperCase().includes(q) ||
              String(r.P_DOCNUMBER || "").toUpperCase().includes(q) ||
              String(r.P_Item_name || "").toUpperCase().includes(q) ||
              String(r.P_CHALAN_NO || "").toUpperCase().includes(q)
            );
          }}
          onChange={(opt) => setSelectedPNumber(opt?.value || "")}
        />

        <div className={styles.topButtons}>
          <button
            className={styles.refreshBtn}
            onClick={fetchList}
            disabled={loading || saving}
          >
            <FaSyncAlt /> {loading ? "Refreshing..." : "Refresh"}
          </button>
          <button
            className={styles.cancelBtn}
            onClick={() => navigate("/dashboard")}
            disabled={saving}
          >
            <FaTimesCircle /> Exit
          </button>
        </div>

        <div className={styles.resultMeta}>{vehicles.length} vehicles loaded</div>
      </div>

      {selected && (
        <div className={styles.panel}>
          <div className={styles.sectionTitle}>Vehicle Details</div>
          <div className={styles.formGrid}>
            <Info label="Vehicle No" value={selected.P_TRUCK} />
            <Info label="Doc Number" value={selected.P_DOCNUMBER} />
            <Info label="Item Name" value={selected.P_Item_name} />
            {/* <Info label="Entity Code" value={pickField(selected, "P_ENTITY_CODE", "P_entity_code")} /> */}
            {/* <Info label="Weigh Type" value={selected.P_Weigh_Type} /> */}

            <div className={styles.formRow2}>
              <Info
                label="First WB No"
                value={pickField(selected, "P_FIRSTWBNO", "p_firstwbno")}
              />
              <Info
                label="Second WB No"
                value={pickField(selected, "P_SECONDWBNO", "p_secondwbno")}
              />
            </div>

            <Info label="First Weight" value={selected.P_FIRSTWT} />
            <Info label="Second Weight" value={selected.P_SECONDWT} />
            <Info label="Net Weight" value={selected.P_NETWT} />

            <Info label="Challan No" value={selected.P_CHALAN_NO} />
            <Info label="Challan Date" value={formatDate(selected.P_CHALLAN_DATE)} />
            <Info
              label="Party Tare Wt"
              value={pickField(selected, "P_PartyTrWt", "P_PARTYTRWT", "p_partytrwt")}
            />
            <Info
              label="Party Gross Wt"
              value={pickField(selected, "P_PartyGrWt", "P_PARTYGRWT", "p_partygrwt")}
            />
            <Info label="Challan Weight" value={selected.P_CHALLAN_WT} />
          </div>

          <div className={styles.actionPanel}>
            <button
              className={styles.submitBtn}
              onClick={handleSubmit}
              disabled={saving}
            >
              <FaSave /> {saving ? "Saving..." : "Submit"}
            </button>
            <button
              className={styles.cancelBtn}
              onClick={handleCancel}
              disabled={saving}
            >
              <FaTimesCircle /> Cancel
            </button>
          </div>
        </div>
      )}

      {!selected && !loading && (
        <div className={styles.emptyBox}>
          Select a vehicle to view Adjust to Challan details.
        </div>
      )}

      <div className={styles.fabStack}>
        <button
          className={styles.fabBtn}
          onClick={() => topRef.current?.scrollIntoView({ behavior: "smooth" })}
        >
          <FaArrowUp />
        </button>
        <button
          className={styles.fabBtn}
          onClick={() => bottomRef.current?.scrollIntoView({ behavior: "smooth" })}
        >
          <FaArrowDown />
        </button>
        <button
          className={`${styles.fabBtn} ${styles.homeFab}`}
          onClick={() => navigate("/dashboard")}
        >
          <FaHome />
        </button>
      </div>

      <div ref={bottomRef} />
    </div>
  );
};

const Info = ({ label, value }) => {
  const text =
    value === null || value === undefined || String(value).trim() === ""
      ? "-"
      : String(value);
  return (
    <div className={styles.formItem}>
      <label>{label}</label>
      <div className={styles.infoValue}>{text}</div>
    </div>
  );
};

export default AdjustToDispatch;
